import { useCallback, useEffect } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import {
    UPDATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.update-conversation';
import {
    LEAVE_CONVERSATION_API,
    type action as leaveAction,
} from '~/routes/remix-api.chat.leave-conversation';
import { useConversationCache } from './use-conversation-cache';

type UpdateConversationResponse = NonNullable<
    SerializeFrom<typeof action>
>['conversation'];

/**
 * Hook to update the settings of a (direct) conversation.
 */
export function useUpdateConversation({
    conversationID,
    onUpdateSuccess,
    onLeaveSuccess,
}: {
    conversationID: number;
    onUpdateSuccess?: (conversation: UpdateConversationResponse) => void;
    onLeaveSuccess?: () => void;
}) {
    const { updateCachedConversation, invalidateCachedConversation } =
        useConversationCache();
    const updateFetcher = useFetcher<typeof action>();
    const leaveFetcher = useFetcher<typeof leaveAction>();
    const latestOnUpdateSuccess = useLatest(onUpdateSuccess);
    const latestOnLeaveSuccess = useLatest(onLeaveSuccess);

    const updateConversation = useCallback(
        ({
            name,
            buildHubID,
        }: {
            name?: string | null;
            buildHubID?: number | null;
        }) => {
            if (conversationID <= 0) return;
            updateFetcher.submit(
                {
                    conversationID,
                    name: name ?? null,
                    buildHubID: buildHubID ?? null,
                },
                {
                    action: UPDATE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [updateFetcher, conversationID]
    );

    const leaveConversation = useCallback(() => {
        if (conversationID <= 0) return;
        leaveFetcher.submit(
            { conversationID },
            {
                action: LEAVE_CONVERSATION_API,
                method: 'POST',
                encType: 'application/json',
            }
        );
    }, [leaveFetcher, conversationID]);

    // Run callback and update cached conversation when update conversation mutation succeeds.
    useEffect(() => {
        if (updateFetcher.data && updateFetcher.state !== 'submitting') {
            const updatedConversation = updateFetcher.data.conversation;
            const systemMessages = updateFetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Name: updatedConversation.Name ?? null,
                BuildHub: updatedConversation.BuildHub ?? null,
                Messages: systemMessages,
            });
            latestOnUpdateSuccess.current?.(updatedConversation);
        }
    }, [updateFetcher, latestOnUpdateSuccess, updateCachedConversation]);

    // Run callback and invalidate cached conversation when leave conversation mutation succeeds.
    useEffect(() => {
        if (leaveFetcher.data && leaveFetcher.state !== 'submitting') {
            const updatedConversation = leaveFetcher.data.conversation;
            // User should no longer see the conversation they left.
            invalidateCachedConversation(Number(updatedConversation.ID));
            latestOnLeaveSuccess.current?.();
        }
    }, [leaveFetcher, latestOnLeaveSuccess, invalidateCachedConversation]);

    return {
        /**
         * One of the update fetchers are submitting.
         */
        isSubmitting:
            updateFetcher.state === 'submitting' ||
            leaveFetcher.state === 'submitting',
        /**
         * Update the conversation settings. Can update the following fields:
         * - Name (title).
         * - BuildHubID. Set to `0` to unlink the project.
         */
        updateConversation,
        /**
         * Leave the conversation. Only works for group chats with more than 2 participants.
         */
        leaveConversation,
    };
}
